import {
  destinationExperiences,
  homepageDestinations,
  type HomepageDestination,
} from '@/lib/homepage-experiences'
import { openingNextDistricts } from '@/lib/live-districts'

export type DistrictSelectorValue = HomepageDestination | 'Madurai' | 'Nilgiris'

export type DistrictStatus = 'live' | 'preview' | 'coming-soon'

export type DistrictMeta = {
  name: DistrictSelectorValue
  status: DistrictStatus
  tagline: string
  character: string
  highlights: string[]
  visitTip: string
  image?: string
  count: number
  themes: string
}

export const DISTRICT_ORDER: DistrictSelectorValue[] = [
  ...homepageDestinations,
  ...openingNextDistricts.filter(
    (district): district is DistrictSelectorValue =>
      !(homepageDestinations as readonly string[]).includes(district),
  ),
]

export const DISTRICT_META: Record<DistrictSelectorValue, Omit<DistrictMeta, 'name' | 'count'>> = {
  Thanjavur: {
    character: 'Silk, soil & dancing dolls',
    highlights: ['Doll making', 'Betel farm walks', 'Silk weaving'],
    status: 'live',
    tagline: 'Living Chola heritage, weaving, traditional crafts and Cauvery delta farm life.',
    image: '/destinations/thanjavur-banner.webp',
    themes: 'Craft · Land · Heritage',
    visitTip: 'For a day of making and discovering: start with a weaving home, then explore the farms and artisan workshops.',
  },
  Coimbatore: {
    character: 'The rhythm of rural life',
    highlights: ['Sirumugai silk', 'Thudumbattam rhythms', 'Fresh neera'],
    status: 'live',
    tagline: 'Working farms, folk rhythms and artisan homes around western Tamil Nadu.',
    image: '/destinations/coimbatore-banner.webp',
    themes: 'Craft · Music · Land',
    visitTip: 'Get closer to the work behind folk instruments and farm traditions, with hosts sharing their everyday skills.',
  },
  Kancheepuram: {
    character: 'Stories woven, performed & served',
    highlights: ['Silk weaving', 'Therukoothu', 'Temple-style idli'],
    status: 'preview',
    tagline: 'Silk, temple food, folk theatre and paddy-field mornings with local experts.',
    image: '/destinations/kancheepuram-banner.webp',
    themes: 'Craft · Food · Heritage',
    visitTip: 'Preview weaving, temple food and folk arts. These experiences are opening next; bookings are not yet available.',
  },
  Madurai: {
    character: 'A taste of the temple streets',
    highlights: ['Food trails', 'Temple streets', 'Craft lanes'],
    status: 'coming-soon',
    tagline: 'Temple streets, food trails and craft lanes — being curated on the ground.',
    image: '/destinations/madurai-banner.webp',
    themes: 'Food · Heritage',
    visitTip: 'A place for curious eaters and heritage lovers. Hosts and routes are still being curated; bookings are not yet open.',
  },
  Nilgiris: {
    character: 'Slow mornings in the hills',
    highlights: ['Tea gardens', 'Forest walks', 'Mountain kitchens'],
    status: 'coming-soon',
    tagline: 'Tea gardens, forest walks and mountain kitchens — opening next.',
    themes: 'Land · Food',
    visitTip: 'For slower mornings and mountain flavours. The local experience collection is being prepared for a future launch.',
  },
}

export function getDistrictMeta(name: DistrictSelectorValue): DistrictMeta {
  return {
    name,
    count: destinationExperiences[name as HomepageDestination]?.length ?? 0,
    ...DISTRICT_META[name],
  }
}

export const DISTRICTS: DistrictMeta[] = DISTRICT_ORDER.map(getDistrictMeta)

export function StatusPill({
  status,
  count,
  onDark,
}: {
  status: DistrictStatus
  count: number
  onDark?: boolean
}) {
  if (status === 'coming-soon') {
    return (
      <span
        className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-[0.6rem] font-bold uppercase tracking-[0.14em] ${
          onDark ? 'bg-white/15 text-white' : 'bg-brand-soft text-brand'
        }`}
      >
        <span className="size-1.5 rounded-full bg-current" aria-hidden="true" />
        Coming soon
      </span>
    )
  }

  return (
    <span
      className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-[0.6rem] font-bold uppercase tracking-[0.14em] ${
        onDark
          ? 'bg-white/15 text-white'
          : status === 'live'
            ? 'bg-green-soft text-primary'
            : 'bg-secondary text-accent-foreground'
      }`}
    >
      <span
        className={`size-1.5 rounded-full ${status === 'live' ? 'bg-emerald-500' : 'bg-brand'}`}
        aria-hidden="true"
      />
      {status === 'live' ? `${count} experiences` : 'Preview'}
    </span>
  )
}
